
import {
  CONTENT_LANGUAGE_COOKIE,
  CONTENT_LANGUAGE_CHANGE_EVENT,
  DEFAULT_CONTENT_LANGUAGE,
  isContentLanguageCode,
  type ContentLanguageCode,
} from "@examora/shared/languages/content-languages";

/**
 * Reads the student's mock-language filter preference from its cookie.
 * Safe to call during SSR/first-render (returns the default rather than
 * throwing) — callers that need to avoid a hydration mismatch should still
 * resolve the real value inside an effect, same pattern as ThemeToggle.
 */
export function getContentLanguageCookie(): ContentLanguageCode {
  if (typeof document === "undefined") return DEFAULT_CONTENT_LANGUAGE;
  const match = document.cookie.match(/(?:^|;\s*)examora_mock_language=([^;]*)/);
  const value = match ? decodeURIComponent(match[1]) : null;
  return isContentLanguageCode(value) ? value : DEFAULT_CONTENT_LANGUAGE;
}

/**
 * Persists the preference for a year and notifies any other mounted
 * component (the exams list fetch effect) via a same-tab custom event —
 * a plain cookie write doesn't fire `storage` events in the tab that wrote
 * it, only in *other* tabs, so a manual event is the only way for the
 * switcher (in the header) and the list (in the page body) to stay in
 * sync without a shared React context.
 */
export function setContentLanguageCookie(code: ContentLanguageCode): void {
  if (typeof document === "undefined") return;
  document.cookie = `${CONTENT_LANGUAGE_COOKIE}=${encodeURIComponent(code)}; path=/; max-age=31536000; SameSite=Lax`;
  window.dispatchEvent(new CustomEvent(CONTENT_LANGUAGE_CHANGE_EVENT, { detail: code }));
}
